import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["active", "use"],
  _excluded2 = ["active", "disabled", "icon", "TabLink", "onClick", "title", "tabId", "tooltip", "tooltipPlacement", "tooltipProps", "theme", "use", "cssStyles"];
import { PureComponent } from 'react';
import styled, { withTheme } from 'foundations-theming/utils';
import { deepMerge } from '../../utils/deepMerge';
import { filterProps } from '../../../utils/filterProps';
import { MERCURY_LAYER, TAB_INDICATOR_HEIGHT, TABS_ACTIVATE_TRANSITION_TIMING } from '../../constants';
import { getSelectedTabBackgroundColor, isContained } from './tabUtils';
import { toPx } from '../../../utils/toPx';
import { TabProvider } from './TabProvider';
import { useTabsContext } from './TabsProvider';
import { callIfPossible } from '../../utils/functions';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { setObjectFontSmoothing } from '../../utils/styles/styles';
import { setUiTransitionStyles } from '../../utils/styles/styles';
import { IconHolder } from '../IconHolder/IconHolder';
import { Link } from '../Link/Link';
import { Tooltip } from '../Tooltip';
import { withTokenProps } from '../../../utils/withTokenProps';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TabIndicator = styled.span.withConfig({
  displayName: "Tab__TabIndicator"
})(["", ";background-color:currentColor;border-radius:24px;bottom:", ";height:", ";left:0;opacity:", ";position:absolute;width:100%;z-index:", ";"], setUiTransitionStyles(), toPx(parseInt(TAB_INDICATOR_HEIGHT, 10) / 2 * -1), TAB_INDICATOR_HEIGHT, ({
  tabIsActive
}) => tabIsActive ? '1 !important' : '0', ({
  tabIsActive
}) => tabIsActive ? MERCURY_LAYER : null);
const getFontWeight = (theme, isActive) => isActive ? {
  fontWeight: theme.typography['display-100'].fontWeight,
  '&:hover': {
    fontWeight: theme.typography['display-100'].fontWeight
  }
} : {
  fontWeight: theme.typography['body-300'].fontWeight
};
const getHoverColor = (theme, active) => {
  return !active ? {
    '&:hover': {
      color: `${theme.color['border-core-hover']} !important`
    }
  } : null;
};
const getBackgroundColor = (use, active) => {
  if (active) {
    return getSelectedTabBackgroundColor(use);
  }
  return use === 'enclosed-shaded' ? 'fill-field-disabled' : 'fill-transitional-tabs-pressed';
};
const getContainedStyles = (theme, use, active) => {
  if (!isContained(use)) return null;
  const base = {
    transition: `background-color ${TABS_ACTIVATE_TRANSITION_TIMING} cubic-bezier(0.25, 0.1, 0.25, 1)`,
    backgroundColor: theme.color[getBackgroundColor(use, active)],
    borderLeft: `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}`,
    borderRadius: '0',
    '&:first-child': {
      borderLeft: 0,
      borderTopLeftRadius: 'inherit'
    },
    '&:last-of-type': {
      borderTopRightRadius: 'inherit'
    },
    [`.${TabIndicator.styledComponentId}`]: {
      display: 'none'
    }
  };
  if (active) {
    base.borderBottomColor = theme.color[getSelectedTabBackgroundColor(use)];
    base['&::after'] = {
      background: `${theme.color[getSelectedTabBackgroundColor(use)]} !important`,
      bottom: `-${theme.borderWidth['100']}`,
      // Aligns with the border width
      content: "' '",
      height: theme.borderWidth['100'],
      // Aligns with the border width
      left: 0,
      position: 'absolute',
      width: '100%'
    };
  }
  return base;
};
const TabStyledLink = props => {
  const rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx(Link, Object.assign({
    use: "unstyled"
  }, rest));
};
class TabComponent extends PureComponent {
  constructor(...args) {
    super(...args);
    this.handleClick = evt => {
      const {
        onClick,
        tabId
      } = this.props;
      callIfPossible(onClick, syntheticEvent(tabId, evt));
    };
  }
  render() {
    const _this$props = this.props,
      {
        active,
        disabled,
        icon,
        TabLink = TabStyledLink,
        title,
        tabId,
        tooltip,
        tooltipPlacement,
        tooltipProps,
        theme,
        use,
        cssStyles
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded2);
    const {
      domProps
    } = filterProps(rest);
    const tabIconStyleOverrides = {
      color: disabled ? theme.color['icon-primary-disabled'] : active ? theme.color['icon-core-default'] : theme.color['icon-core-subtle'],
      paddingBlock: theme.space['0'],
      paddingInline: theme.space['0'],
      marginRight: theme.space['200']
    };
    const tabLinkStyleOverrides = {
      ['&[data-tab-link]']: deepMerge(Object.assign({}, getFontWeight(theme, active), setObjectFontSmoothing('auto'), {
        fontSize: theme.typography['body-100'].fontSize,
        paddingBlock: theme.space['300'],
        paddingInline: theme.space['700'],
        position: 'relative',
        color: disabled ? `${theme.color['text-core-disabled']} !important` : theme.color['text-core-default'],
        transitionProperty: isContained(use) ? 'background-color, color' : 'color',
        whiteSpace: 'nowrap'
      }, use ? getContainedStyles(theme, use, active) : {}, !active ? getHoverColor(theme, active) : {}, {
        '&:focus': {
          textDecoration: 'none',
          zIndex: MERCURY_LAYER
        },
        '&.sr-only-focusable': {
          alignItems: 'center',
          display: 'inline-flex'
        }
      }), this.props.tabsTabStyleOverrides || {},
      // pass cssStyles (consumers token props) here instead of letting them fall through
      // to the ...rest of TabLink so they keep the same level of specificity as
      // data-tab-link styles above
      cssStyles || {})
    };
    const renderedTabContents = /*#__PURE__*/_jsx(TabProvider, {
      tabIconStyleOverrides: tabIconStyleOverrides,
      tabLinkStyleOverrides: tabLinkStyleOverrides,
      children: /*#__PURE__*/_jsxs(TabLink, Object.assign({}, domProps, rest, {
        active: active,
        disabled: disabled,
        "data-tab-id": tabId,
        "data-tab-selected": active,
        "data-tab-link": true // this is used to ONLY target the tab link used in the tabLinkStyleOverrides - a big limitation of our context based styling approach
        ,
        onClick: this.handleClick,
        children: [icon ? /*#__PURE__*/_jsx(IconHolder, {
          icon: icon,
          size: "300"
        }) : null, title, /*#__PURE__*/_jsx(TabIndicator, {
          tabIsActive: active
        })]
      }))
    });
    if (!tooltip) return renderedTabContents;
    return /*#__PURE__*/_jsx(Tooltip, Object.assign({
      placement: tooltipPlacement,
      title: tooltip
    }, tooltipProps, {
      children: renderedTabContents
    }));
  }
}

// Grabs the tabsTabStyleOverrides from the TabsProvider which allows for full width Tab formatting
function TabComponentWithContext(props) {
  const tabsContext = useTabsContext();
  return /*#__PURE__*/_jsx(TabComponent, Object.assign({}, props, {
    tabsTabStyleOverrides: tabsContext === null || tabsContext === void 0 ? void 0 : tabsContext.tabsTabStyleOverrides
  }));
}
export const Tab = withTheme(withTokenProps(TabComponentWithContext));
Tab.displayName = 'Tab';